import React, { useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid } from '@react-three/drei';
import {
  ItemDefinition,
  PlayerInventoryItem,
  TankConfiguration,
  ModuleSlot,
} from '@tankworld/shared';
import {
  STANDARD_TANK_ARMOR_PROFILES,
  TankArmorProfile,
  ArmorZone,
} from '@tankworld/armor-physics';
import { useGameStore } from '../../state/game-store.js';
import { soundSystem } from '../../audio/sound-system.js';
import {
  ArrowLeft,
  Shield,
  Layers,
  Wrench,
  Gauge,
  Zap,
  Weight,
  Eye,
  Crosshair,
  Save,
  CheckCircle,
  AlertTriangle,
  RotateCcw,
  Sparkles,
  Info,
} from 'lucide-react';

interface GarageArmoryProps {
  onBack: () => void;
}

// 3D Tank Garage Model with Heat Map visualization
const GarageTank3D: React.FC<{
  tankClassId: string;
  isHeatMap: boolean;
  selectedZone: ArmorZone | null;
  onSelectZone: (zone: ArmorZone) => void;
}> = ({ tankClassId, isHeatMap, selectedZone, onSelectZone }) => {
  const profile: TankArmorProfile =
    STANDARD_TANK_ARMOR_PROFILES[tankClassId] || STANDARD_TANK_ARMOR_PROFILES.mbt_standard;

  const getHeatMapColor = (thickness: number): string => {
    // Red (thin, <80mm) -> Yellow (medium, 80-250mm) -> Green/Cyan (heavy composite, >300mm)
    if (thickness < 80) return '#ef4444';
    if (thickness < 150) return '#f59e0b';
    if (thickness < 300) return '#10b981';
    return '#06b6d4';
  };

  return (
    <group position={[0, 0, 0]}>
      {/* Visual Base Hull */}
      <mesh position={[0, 0.7, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.8, 0.8, 6.2]} />
        <meshStandardMaterial
          color={isHeatMap ? '#1e293b' : '#334155'}
          roughness={0.8}
          metalness={0.2}
          wireframe={isHeatMap}
        />
      </mesh>

      {/* Visual Base Turret */}
      <mesh position={[0, 1.45, 0.2]} castShadow receiveShadow>
        <boxGeometry args={[2.2, 0.7, 2.6]} />
        <meshStandardMaterial
          color={isHeatMap ? '#1e293b' : '#475569'}
          roughness={0.7}
          metalness={0.3}
          wireframe={isHeatMap}
        />
      </mesh>

      {/* Visual Main Cannon */}
      <mesh position={[0, 1.5, 2.8]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <cylinderGeometry args={[0.09, 0.12, 4.2, 16]} />
        <meshStandardMaterial color="#1e293b" metalness={0.6} roughness={0.4} />
      </mesh>

      {/* Tracks */}
      <mesh position={[-1.5, 0.35, 0]} castShadow>
        <boxGeometry args={[0.5, 0.7, 6.4]} />
        <meshStandardMaterial color="#0f172a" roughness={0.9} />
      </mesh>
      <mesh position={[1.5, 0.35, 0]} castShadow>
        <boxGeometry args={[0.5, 0.7, 6.4]} />
        <meshStandardMaterial color="#0f172a" roughness={0.9} />
      </mesh>

      {/* Armor Scheme Hitboxes (Interactive overlay) */}
      {profile.zones.map((zone) => {
        if (!zone.boundsLocal) return null;
        const b = zone.boundsLocal;
        const sizeX = Math.abs(b.max.x - b.min.x);
        const sizeY = Math.abs(b.max.y - b.min.y);
        const sizeZ = Math.abs(b.max.z - b.min.z);
        const posX = (b.min.x + b.max.x) / 2;
        const posY = (b.min.y + b.max.y) / 2;
        const posZ = (b.min.z + b.max.z) / 2;

        const isSelected = selectedZone?.armorId === zone.armorId;
        const color = isHeatMap ? getHeatMapColor(zone.thicknessMm) : isSelected ? '#10b981' : '#64748b';

        return (
          <mesh
            key={zone.armorId}
            position={[posX, posY, posZ]}
            onClick={(e) => {
              e.stopPropagation();
              soundSystem.playUiClick();
              onSelectZone(zone);
            }}
          >
            <boxGeometry args={[sizeX, sizeY, sizeZ]} />
            <meshStandardMaterial
              color={color}
              transparent
              opacity={isSelected ? 0.75 : isHeatMap ? 0.65 : 0.25}
              roughness={0.2}
              metalness={0.4}
              wireframe={!isSelected && !isHeatMap}
            />
          </mesh>
        );
      })}
    </group>
  );
};

export const GarageArmory: React.FC<GarageArmoryProps> = ({ onBack }) => {
  const token = useGameStore((s) => s.token);
  const selectedTankClass = useGameStore((s) => s.selectedTankClass);
  const setSelectedTankClass = useGameStore((s) => s.setSelectedTankClass);

  const [activeTab, setActiveTab] = useState<'modules' | 'ammo' | 'armor'>('modules');
  const [isHeatMap, setIsHeatMap] = useState(false);
  const [catalog, setCatalog] = useState<ItemDefinition[]>([]);
  const [inventory, setInventory] = useState<PlayerInventoryItem[]>([]);
  const [configurations, setConfigurations] = useState<TankConfiguration[]>([]);
  const [selectedZone, setSelectedZone] = useState<ArmorZone | null>(null);

  // Ammo allocation (max 40 rounds)
  const [apCount, setApCount] = useState(20);
  const [heatCount, setHeatCount] = useState(12);
  const [heCount, setHeCount] = useState(8);

  const [feedbackMsg, setFeedbackMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Load catalog and player inventory
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await fetch('/api/v1/catalog/items');
        if (res.ok) {
          const data = await res.json();
          setCatalog(data.items || []);
        }
      } catch (err) {
        console.warn('Failed to fetch catalog:', err);
      }
    };

    const fetchInventory = async () => {
      if (!token) return;
      try {
        const res = await fetch('/api/v1/inventory', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setInventory(data.inventory || []);
          setConfigurations(data.configurations || []);

          // Find active configuration ammo
          const activeConfig = data.configurations?.find(
            (c: TankConfiguration) => c.tankClassId === selectedTankClass
          );
          if (activeConfig && activeConfig.ammoLoadout) {
            for (const load of activeConfig.ammoLoadout) {
              if (load.ammoItemId === 'ammo_apfsds_3bm42' || load.ammoItemId === 'ap_3bm42') setApCount(load.count);
              if (load.ammoItemId === 'ammo_heat_3bk18m' || load.ammoItemId === 'heat_3bk18m') setHeatCount(load.count);
              if (load.ammoItemId === 'ammo_he_3of26' || load.ammoItemId === 'he_3of26') setHeCount(load.count);
            }
          }
        }
      } catch (err) {
        console.warn('Failed to fetch inventory:', err);
      }
    };

    fetchCatalog();
    fetchInventory();
  }, [token, selectedTankClass]);

  // Set default selected zone
  useEffect(() => {
    const profile =
      STANDARD_TANK_ARMOR_PROFILES[selectedTankClass] || STANDARD_TANK_ARMOR_PROFILES.mbt_standard;
    if (profile && profile.zones.length > 0) {
      setSelectedZone(profile.zones[0]);
    }
  }, [selectedTankClass]);

  const activeConfig = configurations.find((c) => c.tankClassId === selectedTankClass);
  const currentProfile =
    STANDARD_TANK_ARMOR_PROFILES[selectedTankClass] || STANDARD_TANK_ARMOR_PROFILES.mbt_standard;

  // Real-time weight limit computation
  const baseWeightKg = 41500;
  const equippedModulesWeight = Object.values(activeConfig?.equippedModules || {}).reduce(
    (sum: number, mod: any) => sum + (mod?.weightKg || 0),
    0
  );
  const equippedWeightKg =
    baseWeightKg +
    equippedModulesWeight +
    (apCount * 21 + heatCount * 25 + heCount * 28);
  const maxLoadKg = 48000;
  const weightPercentage = Math.min(100, Math.round((equippedWeightKg / maxLoadKg) * 100));

  // Mobility calculations
  const enginePowerHp = 780;
  const powerToWeightRatio = (enginePowerHp / (equippedWeightKg / 1000)).toFixed(1);
  const estimatedMaxSpeed = Math.round(52 - (equippedWeightKg - 40000) * 0.0008);

  const totalAmmo = apCount + heatCount + heCount;

  const handleSaveAmmo = async () => {
    if (!token || !activeConfig) {
      setFeedbackMsg({ type: 'ok', text: 'Cơ số đạn đã được cập nhật thành công (Chế độ cục bộ).' });
      setTimeout(() => setFeedbackMsg(null), 3000);
      return;
    }

    if (totalAmmo > 40) {
      setFeedbackMsg({ type: 'err', text: 'Vượt quá dung lượng hộc đạn tối đa 40 viên!' });
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch('/api/v1/inventory/ammo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tankConfigId: activeConfig.id,
          loadout: [
            { ammoItemId: 'ammo_apfsds_3bm42', count: apCount },
            { ammoItemId: 'ammo_heat_3bk18m', count: heatCount },
            { ammoItemId: 'ammo_he_3of26', count: heCount },
          ],
        }),
      });

      if (res.ok) {
        setFeedbackMsg({ type: 'ok', text: 'Đã lưu cơ số đạn tác chiến vào kho bền vững!' });
      } else {
        const data = await res.json();
        setFeedbackMsg({ type: 'err', text: data.error || 'Lỗi lưu cơ số đạn.' });
      }
    } catch {
      setFeedbackMsg({ type: 'err', text: 'Lỗi mạng khi lưu cấu hình.' });
    } finally {
      setIsSaving(false);
      setTimeout(() => setFeedbackMsg(null), 3500);
    }
  };

  const handleEquipModule = async (slotType: string, itemId: string) => {
    if (!token || !activeConfig) return;
    try {
      const res = await fetch('/api/v1/inventory/equip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tankConfigId: activeConfig.id,
          slotType,
          itemId,
        }),
      });
      if (res.ok) {
        soundSystem.playUiClick();
        setFeedbackMsg({ type: 'ok', text: `Đã lắp module ${itemId} vào slot ${slotType}!` });
      }
    } catch {
      setFeedbackMsg({ type: 'err', text: 'Không thể trang bị module này.' });
    }
    setTimeout(() => setFeedbackMsg(null), 3000);
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col justify-between bg-zinc-950 font-mono text-zinc-100 overflow-hidden">
      {/* Top Header Bar */}
      <div className="p-4 sm:p-6 border-b border-zinc-800 flex flex-wrap justify-between items-center gap-4 bg-zinc-950/90 z-20">
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              soundSystem.playUiClick();
              onBack();
            }}
            className="p-2.5 rounded bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-700 transition-all"
            title="Quay lại Menu Chính"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Wrench className="w-6 h-6 text-emerald-500" />
              <h2 className="text-xl sm:text-2xl font-bold tracking-wider uppercase">
                3D GARAGE & KHO TRANG BỊ
              </h2>
            </div>
            <p className="text-xs text-zinc-400">
              Quản lý mô hình xe tăng, lắp ráp module giáp, cơ số đạn và kiểm tra vật lý kỹ thuật
            </p>
          </div>
        </div>

        {/* View Mode Toggle: Normal vs Technical Heat Map */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              soundSystem.playUiClick();
              setIsHeatMap(!isHeatMap);
            }}
            className={`px-4 py-2 rounded text-xs font-bold uppercase tracking-wider flex items-center gap-2 border transition-all ${
              isHeatMap
                ? 'bg-cyan-950/90 text-cyan-400 border-cyan-500 shadow-lg shadow-cyan-950/60'
                : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-700'
            }`}
          >
            <Layers className="w-4 h-4" />
            {isHeatMap ? 'BẢN ĐỒ NHIỆT GIÁP (BẬT)' : 'CHẾ ĐỘ QUAN SÁT THƯỜNG'}
          </button>

          {/* Tank Switcher */}
          <select
            value={selectedTankClass}
            onChange={(e) => {
              soundSystem.playUiClick();
              setSelectedTankClass(e.target.value);
            }}
            className="p-2 bg-zinc-900 border border-zinc-700 rounded text-xs text-emerald-400 font-bold focus:outline-none focus:border-emerald-500 font-mono"
          >
            <option value="mbt_standard">T-72M (Liên Xô / MBT)</option>
            <option value="mbt_heavy">M1A1 Abrams (Mỹ / Heavy)</option>
            <option value="light_scout">Leopard 2A4 (Đức / Modern)</option>
          </select>
        </div>
      </div>

      {/* Main Workspace Layout */}
      <div className="flex-1 relative flex flex-col lg:flex-row overflow-hidden">
        {/* 3D WebGL Canvas Area */}
        <div className="flex-1 relative bg-gradient-to-b from-zinc-900/60 via-zinc-950/80 to-zinc-950">
          <Canvas camera={{ position: [5, 3.5, 6], fov: 45 }}>
            <ambientLight intensity={0.8} />
            <directionalLight position={[10, 15, 10]} intensity={1.5} castShadow />
            <directionalLight position={[-10, 10, -5]} intensity={0.5} color="#38bdf8" />
            <Grid
              position={[0, 0, 0]}
              args={[20, 20]}
              cellSize={0.5}
              cellThickness={1.0}
              cellColor="#334155"
              sectionSize={2.5}
              sectionThickness={1.5}
              sectionColor="#10b981"
              fadeDistance={25}
            />
            <GarageTank3D
              tankClassId={selectedTankClass}
              isHeatMap={isHeatMap}
              selectedZone={selectedZone}
              onSelectZone={(z) => setSelectedZone(z)}
            />
            <OrbitControls minDistance={3} maxDistance={14} maxPolarAngle={Math.PI / 2 + 0.05} />
          </Canvas>

          {/* 3D Viewport HUD Overlay Instructions */}
          <div className="absolute top-4 left-4 p-3 rounded-lg bg-zinc-950/80 border border-zinc-800 text-xs text-zinc-400 pointer-events-none space-y-1">
            <div className="font-bold text-zinc-200 uppercase text-[11px] flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5 text-emerald-400" /> Điều khiển camera 3D
            </div>
            <div>&bull; Chuột trái: Xoay quỹ đạo quanh xe</div>
            <div>&bull; Cuộn chuột: Phóng to / thu nhỏ</div>
            <div>&bull; Click vào vùng giáp: Xem thông số phân tích</div>
          </div>

          {/* Heat Map Legend (when active) */}
          {isHeatMap && (
            <div className="absolute bottom-4 left-4 p-3 rounded-lg bg-zinc-950/90 border border-cyan-800/60 text-xs font-mono space-y-2">
              <div className="text-[11px] font-bold text-cyan-300 uppercase">Thang đo độ dày giáp RHA:</div>
              <div className="flex items-center gap-3 text-[10px]">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500 inline-block" /> &lt; 80mm</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500 inline-block" /> 80 - 150mm</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500 inline-block" /> 150 - 300mm</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-cyan-500 inline-block" /> &gt; 300mm</span>
              </div>
            </div>
          )}
        </div>

        {/* Right Operations & Customization Sidebar */}
        <div className="w-full lg:w-96 border-l border-zinc-800 bg-zinc-950/95 flex flex-col justify-between overflow-y-auto p-5 space-y-5 z-20">
          {/* Top Sidebar: Tabs */}
          <div>
            <div className="flex border-b border-zinc-800 gap-1 pb-2 mb-4">
              <button
                onClick={() => {
                  soundSystem.playUiClick();
                  setActiveTab('modules');
                }}
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded transition-all ${
                  activeTab === 'modules'
                    ? 'bg-emerald-600 text-zinc-950'
                    : 'bg-zinc-900 text-zinc-400 hover:text-zinc-200'
                }`}
              >
                Module
              </button>
              <button
                onClick={() => {
                  soundSystem.playUiClick();
                  setActiveTab('ammo');
                }}
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded transition-all ${
                  activeTab === 'ammo'
                    ? 'bg-emerald-600 text-zinc-950'
                    : 'bg-zinc-900 text-zinc-400 hover:text-zinc-200'
                }`}
              >
                Cơ số đạn
              </button>
              <button
                onClick={() => {
                  soundSystem.playUiClick();
                  setActiveTab('armor');
                }}
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded transition-all ${
                  activeTab === 'armor'
                    ? 'bg-emerald-600 text-zinc-950'
                    : 'bg-zinc-900 text-zinc-400 hover:text-zinc-200'
                }`}
              >
                Phân tích giáp
              </button>
            </div>

            {/* TAB 1: MODULES & SLOTS */}
            {activeTab === 'modules' && (
              <div className="space-y-4">
                <div className="text-xs text-zinc-400">
                  Trang bị các module hệ thống để nâng cao khả năng cơ động, bảo vệ và hỏa lực:
                </div>

                <div className="space-y-3">
                  {[
                    { slot: 'hull', name: 'Thân xe (Hull)', item: 'Giáp thép cán nguội RHA' },
                    { slot: 'turret', name: 'Tháp pháo (Turret)', item: 'Tháp pháo đúc composite' },
                    { slot: 'gun', name: 'Pháo chính (Main Gun)', item: '125mm 2A46 Smoothbore' },
                    { slot: 'engine', name: 'Động cơ (Engine)', item: 'V-46-6 V12 Diesel (780 hp)' },
                    { slot: 'transmission', name: 'Truyền động (Transmission)', item: 'Hộp số hành tinh 7 cấp' },
                    { slot: 'tracks', name: 'Xích & Hệ thống treo', item: 'Thanh xoắn thủy lực giảm chấn' },
                  ].map((m) => (
                    <div
                      key={m.slot}
                      className="p-3 rounded bg-zinc-900/60 border border-zinc-800 flex justify-between items-center text-xs"
                    >
                      <div>
                        <div className="text-zinc-400 font-bold uppercase text-[10px]">{m.name}</div>
                        <div className="text-zinc-200 font-bold mt-0.5">{m.item}</div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-950/80 text-emerald-400 border border-emerald-800 font-bold">
                        ĐÃ LẮP
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 2: AMMUNITION LOADOUT */}
            {activeTab === 'ammo' && (
              <div className="space-y-5">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-zinc-400 uppercase font-bold">Tổng cơ số đạn:</span>
                  <span
                    className={`font-bold px-2 py-0.5 rounded text-xs ${
                      totalAmmo > 40 ? 'bg-rose-950 text-rose-400 border border-rose-700' : 'text-emerald-400'
                    }`}
                  >
                    {totalAmmo} / 40 viên
                  </span>
                </div>

                {/* Shell Type Sliders */}
                <div className="space-y-4">
                  {/* APFSDS */}
                  <div className="p-3.5 rounded bg-zinc-900/60 border border-zinc-800 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-zinc-100">125mm 3BM42 APFSDS</span>
                      <span className="text-emerald-400 font-bold">{apCount} viên</span>
                    </div>
                    <div className="text-[10px] text-zinc-400">
                      Đạn động năng xuyên giáp cao tốc (1700 m/s &bull; 460mm RHA)
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={40}
                      value={apCount}
                      onChange={(e) => setApCount(Number(e.target.value))}
                      className="w-full accent-emerald-500"
                    />
                  </div>

                  {/* HEAT */}
                  <div className="p-3.5 rounded bg-zinc-900/60 border border-zinc-800 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-zinc-100">125mm 3BK18M HEAT-FS</span>
                      <span className="text-amber-400 font-bold">{heatCount} viên</span>
                    </div>
                    <div className="text-[10px] text-zinc-400">
                      Đạn nổ lõm chống tăng (905 m/s &bull; 550mm RHA xuyên phá)
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={40}
                      value={heatCount}
                      onChange={(e) => setHeatCount(Number(e.target.value))}
                      className="w-full accent-amber-500"
                    />
                  </div>

                  {/* HE */}
                  <div className="p-3.5 rounded bg-zinc-900/60 border border-zinc-800 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-zinc-100">125mm 3OF26 HE-FRAG</span>
                      <span className="text-blue-400 font-bold">{heCount} viên</span>
                    </div>
                    <div className="text-[10px] text-zinc-400">
                      Đạn nổ phá mảnh sát thương diện rộng (3.4kg thuốc nổ TNT)
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={40}
                      value={heCount}
                      onChange={(e) => setHeCount(Number(e.target.value))}
                      className="w-full accent-blue-500"
                    />
                  </div>
                </div>

                <button
                  onClick={handleSaveAmmo}
                  disabled={isSaving || totalAmmo > 40}
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 font-bold text-xs uppercase tracking-wider rounded flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {isSaving ? 'Đang lưu...' : 'Lưu cơ số đạn tác chiến'}
                </button>
              </div>
            )}

            {/* TAB 3: ARMOR SCHEME INSPECTION */}
            {activeTab === 'armor' && (
              <div className="space-y-4 text-xs">
                {selectedZone ? (
                  <div className="p-4 rounded bg-zinc-900/80 border border-emerald-800/60 space-y-3">
                    <div className="border-b border-zinc-800 pb-2">
                      <span className="text-[10px] text-zinc-400 uppercase">Vùng giáp được chọn</span>
                      <h4 className="text-base font-bold text-emerald-400">{selectedZone.name}</h4>
                    </div>

                    <div className="space-y-2 font-mono text-[11px]">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Độ dày danh nghĩa:</span>
                        <span className="text-zinc-100 font-bold">{selectedZone.thicknessMm} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Hình học bề mặt:</span>
                        <span className="text-zinc-100 capitalize">{selectedZone.surfaceGeometry}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Loại vật liệu:</span>
                        <span className="text-zinc-100 uppercase">{selectedZone.materialId}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Cấu trúc lớp:</span>
                        <span className="text-zinc-100">
                          {selectedZone.layers?.length || 1} lớp vật liệu
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Góc nghiêng danh nghĩa:</span>
                        <span className="text-zinc-100">
                          {Math.round(Math.acos(Math.abs(selectedZone.normalLocal.z)) * (180 / Math.PI))}°
                        </span>
                      </div>
                    </div>

                    {/* Multi-layer list if applicable */}
                    {selectedZone.layers && selectedZone.layers.length > 0 && (
                      <div className="pt-2 border-t border-zinc-800 space-y-1.5">
                        <div className="text-[10px] font-bold text-zinc-400 uppercase">Chi tiết các lớp giáp:</div>
                        {selectedZone.layers.map((l) => (
                          <div key={l.layerIndex} className="flex justify-between text-[10px] text-zinc-300">
                            <span>Lớp {l.layerIndex + 1} ({l.materialId}):</span>
                            <span>{l.thicknessMm} mm {l.airGapMm > 0 ? `+ ${l.airGapMm}mm khe hở` : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-6 text-center text-zinc-500">
                    Nhấp vào mô hình 3D để chọn vùng giáp phân tích chi tiết.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Bottom Sidebar: Tank Weight, Engine & Mobility Telemetry */}
          <div className="border-t border-zinc-800 pt-4 space-y-3 text-xs">
            {feedbackMsg && (
              <div
                className={`p-2.5 rounded border text-xs flex items-center gap-2 ${
                  feedbackMsg.type === 'ok'
                    ? 'bg-emerald-950/60 border-emerald-700/80 text-emerald-300'
                    : 'bg-rose-950/60 border-rose-700/80 text-rose-300'
                }`}
              >
                {feedbackMsg.type === 'ok' ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
                )}
                <span>{feedbackMsg.text}</span>
              </div>
            )}

            {/* Weight Bar */}
            <div>
              <div className="flex justify-between text-[11px] mb-1">
                <span className="text-zinc-400 flex items-center gap-1">
                  <Weight className="w-3.5 h-3.5 text-emerald-400" /> Tải trọng:
                </span>
                <span className="font-bold text-zinc-200">
                  {(equippedWeightKg / 1000).toFixed(1)} / {(maxLoadKg / 1000).toFixed(1)} Tấn ({weightPercentage}%)
                </span>
              </div>
              <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    weightPercentage > 95 ? 'bg-rose-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${weightPercentage}%` }}
                />
              </div>
            </div>

            {/* Mobility Stats Grid */}
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="p-2 rounded bg-zinc-900 border border-zinc-800">
                <span className="text-zinc-400 block">Tỷ số công suất:</span>
                <span className="font-bold text-emerald-400">{powerToWeightRatio} hp/tấn</span>
              </div>
              <div className="p-2 rounded bg-zinc-900 border border-zinc-800">
                <span className="text-zinc-400 block">Tốc độ tối đa:</span>
                <span className="font-bold text-emerald-400">{estimatedMaxSpeed} km/h</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
